import React from 'react';
import { useDragLayer, XYCoord } from 'react-dnd';
import {
  useEditSceneModalStateContext,
  useSlots,
} from 'containers/EditSceneModal/state';
import { DndItemType } from 'containers/EditSceneModal/types';
import {
  block,
  getSlotBorderRadius,
  getSlotRect,
} from 'containers/EditSceneModal/utils';

type Coords = XYCoord | null;

function getItemStyle(
  initialClientOffset: Coords,
  initialSourceOffset: Coords,
  clientOffset: Coords,
): React.CSSProperties {
  if (initialClientOffset === null || clientOffset === null) {
    return { display: 'none' };
  }

  // clacluate where in the element the user clicked (offset from element's top-left)
  const mouseSourceOffsetX = initialClientOffset.x - initialSourceOffset.x;
  const mouseSourceOffsetY = initialClientOffset.y - initialSourceOffset.y;

  // position preview so the click point stays under the cursor.
  // current mouse position minus the offset from where they clicked
  const x = clientOffset.x - mouseSourceOffsetX;
  const y = clientOffset.y - mouseSourceOffsetY;

  return {
    left: 0,
    position: 'fixed',
    top: 0,
    transform: `translate(${x}px, ${y}px)`,
  };
}

const EditSceneOverlaySlotDragPreview: React.FC = () => {
  const {
    initialClientOffset,
    initialSourceOffset,
    clientOffset,
    item,
    itemType,
    isDragging,
  } = useDragLayer(monitor => ({
    sourceOffset: monitor.getSourceClientOffset(),
    initialClientOffset: monitor.getInitialClientOffset(),
    initialSourceOffset: monitor.getInitialSourceClientOffset(),
    clientOffset: monitor.getClientOffset(),
    item: monitor.getItem(),
    itemType: monitor.getItemType(),
    isDragging: monitor.isDragging(),
  }));

  const {
    state: { previewDimensions },
  } = useEditSceneModalStateContext();
  const { slots } = useSlots();

  if (!isDragging || itemType !== DndItemType.SLOT) {
    return null;
  }

  const slotIndex = item?.slotIndex ?? -1;
  const slot = slots?.[slotIndex];

  if (!slot || !previewDimensions) {
    return null;
  }

  const slotRect = getSlotRect(slot, previewDimensions);
  const borderRadius = getSlotBorderRadius(slotRect, previewDimensions);

  return (
    <div
      className={block('slots-overlay-slot-drag-layer', { preview: true })}
      style={{
        ...getItemStyle(initialClientOffset, initialSourceOffset, clientOffset),
        width: slotRect.width,
        height: slotRect.height,
        borderRadius,
      }}
    />
  );
};

export default EditSceneOverlaySlotDragPreview;
